import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Team } from '../../types';
import { teamsAPI, analyticsAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import TeamPerformanceChart from './TeamPerformanceChart';
import toast from 'react-hot-toast';

// Helper functions for project styling
const getProjectStatusColor = (status: string) => {
  switch (status) {
    case 'PLANNING': return 'bg-gray-100 text-gray-800';
    case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
    case 'REVIEW': return 'bg-yellow-100 text-yellow-800';
    case 'COMPLETED': return 'bg-green-100 text-green-800';
    case 'ON_HOLD': return 'bg-orange-100 text-orange-800';
    case 'CANCELLED': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getProjectPriorityColor = (priority: string) => {
  switch (priority) {
    case 'HIGH': return 'bg-red-100 text-red-800';
    case 'NORMAL': return 'bg-blue-100 text-blue-800';
    case 'LOW': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

interface TeamDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
  onTeamUpdate?: (team: Team) => void;
}

interface TeamPerformance {
  team: Team;
  taskStats: Record<string, number>;
  priorityStats: Record<string, number>;
  recentTasks: any[];
  memberPerformance: any[];
}

interface TeamStatistics {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  overdueTasks: number;
  highPriorityTasks: number;
  memberCount: number;
  completionRate: number;
  avgCompletionTimeHours: number;
}

const TeamDetailModal: React.FC<TeamDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  team, 
  onTeamUpdate 
}) => {
  const [performance, setPerformance] = useState<TeamPerformance | null>(null);
  const [statistics, setStatistics] = useState<TeamStatistics | null>(null);
  const [analytics, setAnalytics] = useState<any | null>(null);
  const [teamData, setTeamData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'members' | 'analytics'>('overview');
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && team) {
      loadTeamData();
    }
  }, [isOpen, team]);

  const loadTeamData = async () => {
    if (!team) return;
    
    try {
      setIsLoading(true);
      const [performanceData, statisticsData, analyticsData] = await Promise.all([
        teamsAPI.getTeamPerformance(team.id),
        teamsAPI.getTeamStatistics(team.id),
        analyticsAPI.getTeamAnalytics(team.id),
      ]);
      setPerformance(performanceData);
      setStatistics(statisticsData);
      setAnalytics(analyticsData);

      // Extract team data with projects from performance response
      if (performanceData && performanceData.team) {
        setTeamData(performanceData.team);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!team) return;

    // Prevent removing the team manager
    if (userId === team.manager.id) {
      toast.error('Cannot remove the team manager. Please assign a new manager first.');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    try {
      setRemovingMemberId(userId);
      const updatedTeam = await teamsAPI.removeMember(team.id, userId);

      // Update the team data and reload performance data
      if (onTeamUpdate) {
        onTeamUpdate(updatedTeam);
      }

      // Reload team data to refresh member performance
      await loadTeamData();

      toast.success('Member removed successfully');
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'TODO':
        return 'bg-gray-100 text-gray-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!team) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Background overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-6xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: team.color }}
                  >
                    <UserGroupIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{team.name}</h2>
                    <p className="text-gray-600">{team.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span>Manager: {team.manager.firstName} {team.manager.lastName}</span>
                      <span>•</span>
                      <span>{team.members.length} members</span>
                      <span>•</span>
                      <span>{team._count?.tasks || 0} tasks</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
                    { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentListIcon },
                    { id: 'members', name: 'Members', icon: UserGroupIcon },
                    { id: 'analytics', name: 'Analytics', icon: ArrowTrendingUpIcon },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Content */}
              <div className="min-h-96">
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : (
                  <div>
                    {activeTab === 'overview' && statistics && (
                      <div className="space-y-6">
                        {/* Statistics Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <ClipboardDocumentListIcon className="w-8 h-8 text-blue-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-blue-600">Total Tasks</p>
                                <p className="text-2xl font-bold text-blue-900">{statistics.totalTasks}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <ChartBarIcon className="w-8 h-8 text-green-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-green-600">Completed</p>
                                <p className="text-2xl font-bold text-green-900">{statistics.completedTasks}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-yellow-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <CalendarIcon className="w-8 h-8 text-yellow-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-yellow-600">In Progress</p>
                                <p className="text-2xl font-bold text-yellow-900">{statistics.inProgressTasks}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-red-50 p-4 rounded-lg">
                            <div className="flex items-center">
                              <ArrowTrendingUpIcon className="w-8 h-8 text-red-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-red-600">Overdue</p>
                                <p className="text-2xl font-bold text-red-900">{statistics.overdueTasks}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Performance Charts */}
                        {performance && (
                          <TeamPerformanceChart 
                            taskStats={performance.taskStats} 
                            priorityStats={performance.priorityStats} 
                          />
                        )}

                        {/* Team Metrics */}
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Team Metrics</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
                              <div className="mt-2">
                                <div className="flex items-center">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${Math.min(statistics.completionRate, 100)}%` }}
                                    />
                                  </div>
                                  <span className="ml-2 text-sm font-medium text-gray-900">
                                    {statistics.completionRate}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Avg. Completion Time</p>
                              <p className="text-xl font-bold text-gray-900">
                                {statistics.avgCompletionTimeHours}h
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">High Priority Tasks</p>
                              <p className="text-xl font-bold text-gray-900">{statistics.highPriorityTasks}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'tasks' && performance && (
                      <div className="space-y-6">
                        {/* Task Status Distribution */}
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Distribution</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(performance.taskStats).map(([status, count]) => (
                              <div key={status} className="text-center">
                                <div className="text-2xl font-bold text-gray-900">{count}</div>
                                <div className={`text-sm px-2 py-1 rounded-full inline-block mt-1 ${getStatusColor(status)}`}>
                                  {status.replace('_', ' ')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Projects */}
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Projects</h3>
                          <div className="space-y-3">
                            {!teamData?.projects || teamData.projects.length === 0 ? (
                              <p className="text-gray-500 text-center py-8">No projects assigned</p>
                            ) : (
                              teamData.projects.map((projectTeam: any) => (
                                <div key={projectTeam.project.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div
                                        className="w-4 h-4 rounded-full"
                                        style={{ backgroundColor: projectTeam.project.color || '#6B7280' }}
                                      />
                                      <div>
                                        <h4 className="font-medium text-gray-900">{projectTeam.project.name}</h4>
                                        <p className="text-sm text-gray-500">
                                          Created by {projectTeam.project.creator.firstName} {projectTeam.project.creator.lastName} •
                                          {projectTeam.project._count.tasks} task{projectTeam.project._count.tasks !== 1 ? 's' : ''}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getProjectStatusColor(projectTeam.project.status)}`}>
                                        {projectTeam.project.status}
                                      </span>
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getProjectPriorityColor(projectTeam.project.priority)}`}>
                                        {projectTeam.project.priority}
                                      </span>
                                    </div>
                                  </div>
                                  {projectTeam.project.description && (
                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{projectTeam.project.description}</p>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Recent Tasks */}
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Tasks</h3>
                          <div className="space-y-3">
                            {performance.recentTasks.length === 0 ? (
                              <p className="text-gray-500 text-center py-8">No recent tasks</p>
                            ) : (
                              performance.recentTasks.map((task) => (
                                <div key={task.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                                      <div>
                                        <h4 className="font-medium text-gray-900">{task.title}</h4>
                                        <p className="text-sm text-gray-500">
                                          {task.project ? (
                                            <>
                                              <span className="inline-flex items-center space-x-1">
                                                <div
                                                  className="w-2 h-2 rounded-full"
                                                  style={{ backgroundColor: task.project.color || '#6B7280' }}
                                                />
                                                <span className="font-medium">{task.project.name}</span>
                                              </span>
                                              {' • '}
                                            </>
                                          ) : (
                                            <span className="text-gray-400">No Project • </span>
                                          )}
                                          Created by {task.creator.firstName} {task.creator.lastName} •
                                          Updated {new Date(task.updatedAt).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                                      {task.status.replace('_', ' ')}
                                    </span>
                                  </div>
                                  {task.description && (
                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{task.description}</p>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'members' && performance && (
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members Performance</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {performance.memberPerformance.map((member) => (
                              <div key={member.user.id} className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                                      <span className="text-sm font-medium text-white">
                                        {member.user.firstName[0]}{member.user.lastName[0]}
                                      </span>
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-gray-900">
                                        {member.user.firstName} {member.user.lastName}
                                      </h4>
                                    </div>
                                  </div>

                                  {/* Remove Member Button */}
                                  {team && member.user.id === team.manager.id ? (
                                    <div className="p-1 text-gray-400" title="Team Manager (cannot be removed)">
                                      <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        Manager
                                      </span>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleRemoveMember(member.user.id)}
                                      disabled={removingMemberId === member.user.id}
                                      className="p-1 text-red-600 hover:text-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Remove from team"
                                    >
                                      {removingMemberId === member.user.id ? (
                                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        <TrashIcon className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Total Tasks</span>
                                    <span className="font-medium">{member.totalTasks}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Completed</span>
                                    <span className="font-medium">{member.completedTasks}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Completion Rate</span>
                                    <span className="font-medium">{member.completionRate.toFixed(1)}%</span>
                                  </div>

                                  <div className="mt-3">
                                    <div className="flex items-center">
                                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                                        <div
                                          className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                          style={{ width: `${Math.min(member.completionRate, 100)}%` }}
                                        />
                                      </div>
                                      <span className="ml-2 text-xs text-gray-500">
                                        {member.completionRate.toFixed(0)}%
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'analytics' && analytics && (
                      <div className="space-y-6">
                        {/* Team Performance Overview */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="bg-blue-50 p-6 rounded-lg">
                            <div className="flex items-center">
                              <ChartBarIcon className="w-8 h-8 text-blue-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-blue-600">Project Completion Rate</p>
                                <p className="text-2xl font-bold text-blue-900">
                                  {analytics.productivity?.projectCompletionRate?.toFixed(1) || 0}%
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-50 p-6 rounded-lg">
                            <div className="flex items-center">
                              <ArrowTrendingUpIcon className="w-8 h-8 text-green-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-green-600">Task Completion Rate</p>
                                <p className="text-2xl font-bold text-green-900">
                                  {analytics.productivity?.taskCompletionRate?.toFixed(1) || 0}%
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-purple-50 p-6 rounded-lg">
                            <div className="flex items-center">
                              <UserGroupIcon className="w-8 h-8 text-purple-600" />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-purple-600">Active Projects</p>
                                <p className="text-2xl font-bold text-purple-900">
                                  {analytics.projectContribution?.length || 0}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Workload Distribution */}
                        {analytics.workloadDistribution && analytics.workloadDistribution.length > 0 && (
                          <div className="bg-white p-6 rounded-lg border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Distribution</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {analytics.workloadDistribution.map((item: any) => (
                                <div key={item.status} className="text-center">
                                  <div className="text-2xl font-bold text-gray-900">{item.count}</div>
                                  <div className={`text-sm px-2 py-1 rounded-full inline-block mt-1 ${getStatusColor(item.status)}`}>
                                    {item.status.replace('_', ' ')}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Member Performance */}
                        {analytics.memberPerformance && analytics.memberPerformance.length > 0 && (
                          <div className="bg-white p-6 rounded-lg border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Member Performance</h3>
                            <div className="space-y-3">
                              {analytics.memberPerformance.map((member: any) => (
                                <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                      <span className="text-sm font-medium text-primary-600">
                                        {member.firstName?.[0]}{member.lastName?.[0]}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {member.firstName} {member.lastName}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {member.completedTasks} completed tasks
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900">
                                      {member.completionRate?.toFixed(1) || 0}%
                                    </p>
                                    <p className="text-xs text-gray-500">completion rate</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Project Contributions */}
                        {analytics.projectContribution && analytics.projectContribution.length > 0 && (
                          <div className="bg-white p-6 rounded-lg border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Project Contributions</h3>
                            <div className="space-y-3">
                              {analytics.projectContribution.map((project: any) => (
                                <div key={project.projectId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className="w-4 h-4 rounded-full"
                                      style={{ backgroundColor: project.projectColor || '#6B7280' }}
                                    />
                                    <div>
                                      <p className="font-medium text-gray-900">{project.projectName}</p>
                                      <p className="text-sm text-gray-500">
                                        Status: {project.projectStatus}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900">
                                      {project.taskCount} tasks
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TeamDetailModal;
